import { Avatar, Icon } from '@/components';
import { initialsOf } from '@/lib/format';
import type { RosterEntry } from '@/types/domain';
import { tier } from './scoreboard';

/**
 * One learner on the Hero Wall — by display picture, never the name.
 *  - pass (≥80): blooms gold with a ✓ and the score (the cert moment)
 *  - close (70–79): warm amber "อีกนิดเดียว!" — acknowledged, NO number
 *  - below (<70): soft "สู้ๆ" — present and cheered, NO number, no red
 */
export function AvatarTile({
  entry,
  size,
  index,
  cascade,
  mode,
}: {
  entry: RosterEntry;
  /** avatar diameter in px */
  size: number;
  index: number;
  /** stagger the entrance during the opening cascade; live arrivals pop immediately */
  cascade: boolean;
  /** 'post' = pass/close/below celebration; 'pre' = neutral baseline */
  mode: 'pre' | 'post';
}) {
  const initials = initialsOf(entry.name.split(' ')[0], entry.name.split(' ')[1] ?? '');
  const delay = cascade ? Math.min(index * 0.06, 2.4) : 0;

  // Pre-test = a "starting line": neutral tile, baseline score, no pass/fail.
  if (mode === 'pre') {
    return (
      <div className="wp-pop" style={{ animationDelay: `${delay}s`, display: 'flex', flexDirection: 'column', alignItems: 'center', width: size + 22 }}>
        <div style={{ borderRadius: '50%', padding: 3, background: 'var(--line)' }}>
          <Avatar initials={initials} size={size} tone={entry.tone ?? 'slate'} photoUrl={entry.photoUrl} />
        </div>
        <div style={{ marginTop: 6, fontSize: Math.max(13, size * 0.28), fontWeight: 700, color: 'var(--ink-2)', lineHeight: 1 }}>{entry.pretest}</div>
      </div>
    );
  }

  const band = tier(entry);
  const pass = band === 'pass';
  const close = band === 'close';
  const badge = Math.round(size * 0.42);

  const ring = pass ? 'var(--sage)' : close ? 'var(--ochre)' : 'var(--line)';
  const glow = pass
    ? '0 0 0 4px var(--sage-tint), 0 6px 16px rgba(127,174,95,.32)'
    : close
      ? '0 0 0 4px var(--ochre-soft)'
      : 'none';

  return (
    <div className="wp-pop" style={{ animationDelay: `${delay}s`, display: 'flex', flexDirection: 'column', alignItems: 'center', width: size + 22 }}>
      <div style={{ position: 'relative' }}>
        <div style={{ borderRadius: '50%', padding: 3, background: ring, boxShadow: glow }}>
          <Avatar initials={initials} size={size} tone={entry.tone ?? 'slate'} photoUrl={entry.photoUrl} />
        </div>
        {pass && (
          <div
            style={{
              position: 'absolute', right: -2, bottom: -2, width: badge, height: badge, borderRadius: '50%',
              background: 'var(--sage)', color: '#fff', display: 'grid', placeItems: 'center', border: '2px solid var(--paper)',
            }}
          >
            <Icon name="check" size={Math.round(badge * 0.62)} />
          </div>
        )}
      </div>
      {pass ? (
        <div style={{ marginTop: 6, fontSize: Math.max(14, size * 0.3), fontWeight: 800, color: 'var(--sage-deep)', lineHeight: 1 }}>{entry.posttest}</div>
      ) : close ? (
        <div className="th" style={{ marginTop: 6, fontSize: Math.max(10, size * 0.18), fontWeight: 600, color: 'var(--ochre)', textAlign: 'center', lineHeight: 1.15 }}>อีกนิดเดียว! 💪</div>
      ) : (
        <div className="th" style={{ marginTop: 6, fontSize: Math.max(11, size * 0.2), color: 'var(--ink-3)', textAlign: 'center', lineHeight: 1.15 }}>สู้ๆ 💪</div>
      )}
    </div>
  );
}
